import type { JobWithRelations } from "@/types/db";
import { companyDisplayName } from "./jobLifecycle";
import { formatLocation, formatSalaryPeriod } from "./utils";
import { getTagListingName } from "./tagCopy";

// Countries normalized by the ingest location parser; unknown regions stay omitted.
const countryNames = new Intl.DisplayNames(["en"], { type: "region" });
const countryCodes = new Map(
  "US CA GB DE FR AU IN IL BR MX AR CO CR NL IE ES IT SE NO DK FI CH AT PT PL JP SG NZ AZ"
    .split(" ")
    .flatMap((code) => [
      [code.toLowerCase(), code],
      [countryNames.of(code)?.toLowerCase() ?? code, code],
    ]),
);
countryCodes.set("usa", "US");
countryCodes.set("uk", "GB");
countryCodes.set("united states of america", "US");

export function countryCode(country: string | null | undefined) {
  return country ? countryCodes.get(country.trim().toLowerCase()) : undefined;
}

export function getJobMeta(job: JobWithRelations) {
  const company = companyDisplayName(job);
  const location = job.isRemote
    ? `Remote${job.country ? ` (${job.country})` : ""}`
    : formatLocation(job);
  const min = job.minSalary?.toLocaleString("en-US");
  const max = job.maxSalary?.toLocaleString("en-US");
  const range =
    min && max
      ? `${min}–${max}`
      : min
        ? `from ${min}`
        : max
          ? `up to ${max}`
          : null;
  const salary =
    job.salaryCurrency && range
      ? `${job.salaryCurrency} ${range}${job.salaryPeriod ? ` / ${formatSalaryPeriod(job.salaryPeriod)}` : ""}`
      : null;
  const skills = job.jobTags
    ?.filter((tag) => tag.category?.slug === "skills")
    .slice(0, 3)
    .map((tag) => tag.name);
  return {
    title:
      [`${job.role} at ${company}`, location, salary]
        .filter(Boolean)
        .join(" - ") + " | Data First Jobs",
    description: [
      `${job.role} at ${company}. ${location}.`,
      salary ? `Salary: ${salary}.` : null,
      skills?.length ? `Skills: ${skills.join(", ")}.` : null,
      job.seniority ? `Seniority: ${job.seniority}.` : null,
      "Explore the role on Data First Jobs.",
    ]
      .filter(Boolean)
      .join(" "),
  };
}

export function getJobBreadcrumbs(job: JobWithRelations) {
  const tag = job.jobTags?.find(
    (tag) => tag.category?.slug === "position-group",
  );
  return [
    { label: "Home", href: "/" },
    ...(tag
      ? [
          { label: getTagListingName(tag), href: `/${tag.slug}` },
          ...(job.isRemote
            ? [
                {
                  label: getTagListingName(tag, { isRemote: true }),
                  href: `/remote/${tag.slug}`,
                },
              ]
            : []),
        ]
      : []),
    { label: `${companyDisplayName(job)} - ${job.role}` },
  ];
}
